import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { Subscription, interval, switchMap } from 'rxjs';
import { TracerService } from '../../../services/tracer.service';
import { CollapseProvidersPipe } from '../../../pipes/collapse-providers-pipe';
import { ModalDialogDirective } from '../../../directives/modal-dialog.directive';
import {
  PacingMode,
  ReplayEvent,
  ReplayPlanEntry,
  ReplayRequestResult,
  ReplayVerbosity,
  ResultIO,
  SessionReplay,
  StartReplayBody,
} from '../../../models/replay.model';

type PanelStep = 'plan' | 'running' | 'result';

// Append-only operational log surfaced under the result list so
// operators can copy a self-contained debug trace without exposing
// payloads. Lines come from SSE events for live runs; deep-linked
// existing replays rebuild a coarser version from the snapshot
// (terminal events only — we don't have step history for past runs).
//
// Payloads/headers/snippets/bodies are deliberately excluded — the
// log is "what fired and how it ended", not "what was in the
// envelope". Use the verbose I/O view for that.
type LogLevel = 'info' | 'warn' | 'error';
interface ReplayLogLine {
  ts: string;
  level: LogLevel;
  msg: string;
}

@Component({
  selector: 'app-replay-panel',
  imports: [CommonModule, FormsModule, RouterLink, CollapseProvidersPipe, ModalDialogDirective],
  templateUrl: './replay-panel.html',
  styleUrl: './replay-panel.scss',
})
export class ReplayPanel implements OnInit, OnDestroy {
  @Input() sessionValue: string = '';
  /**
   * Optional replay_id to deep-link the panel into a past run. When set,
   * ngOnInit skips the plan step and loads that replay's snapshot
   * directly into the result view. Used by the recent-replays chip
   * strip in RequestDetails.
   */
  @Input() initialReplayId: number | null = null;
  @Output() closePanel = new EventEmitter<void>();

  // Step machine
  step: PanelStep = 'plan';
  /**
   * Verbosity is BOTH a kickoff parameter (drives backend persistence
   * + step-event volume) AND the post-hoc UI tier selector. Default
   * 'detailed' so the typical run captures the snippet + step events
   * without paying for the captured-request join.
   */
  verbosity: ReplayVerbosity = 'detailed';

  // Plan step state
  planLoading = false;
  planError: string | null = null;
  entries: ReplayPlanEntry[] = [];
  defaultDateShiftDays = 0;
  defaultRequestTimeoutMs = 30_000;
  /** request_id -> selected (true/false). Bound to the checklist. */
  selected: Record<number, boolean> = {};
  /** Configurable kickoff parameters. */
  dateShiftDays = 0;
  pacingMode: PacingMode = 'sequential';
  pacingFFwdMs = 1_000;
  // 60 s default — AirShopping fan-outs on staging routinely take
  // 30-50 s (replay #29 = 45 s), so 30 s was tripping legitimate
  // requests into context-deadline. fetchPlan still overwrites this
  // with the BE's Replay.TimeoutSeconds when that comes in (also
  // bumped to 60 in tracer MR !408); the FE-side seed is for the
  // case where the plan response hasn't arrived yet.
  requestTimeoutSeconds = 60;
  continueOnError = true;
  paramsExpanded = false;

  // Running / result state
  startError: string | null = null;
  replay: SessionReplay | null = null;
  resultsByRequestId: Record<number, ReplayRequestResult> = {};
  /**
   * Latest activity phase per row, indexed by original_request_id.
   * Wired to step events from the SSE stream so the UI updates every
   * time the executor crosses a phase boundary mid-request.
   */
  liveStepByRequestId: Record<number, string> = {};
  /** Verbose-view payloads, lazy-loaded per result. */
  ioByResultId: Record<number, ResultIO | { loading: true } | { error: string }> = {};
  expandedResultIds: Record<number, boolean> = {};

  private streamSub: Subscription | null = null;
  private pollSub: Subscription | null = null;

  /** Append-only log lines surfaced in the "Replay log" section. */
  replayLog: ReplayLogLine[] = [];
  copiedLog = false;
  /** Step-dedup so the executor's repeated phase emissions don't flood the log. */
  private lastLoggedStep: Record<number, string> = {};
  /** One-shot guards to keep init/end lines unique per replay regardless
   *  of how many SSE frames carry the replay snapshot. The init and
   *  start lines are gated separately because the BE may emit a snapshot
   *  with started_at still empty (the run hasn't crossed the executor
   *  start boundary yet) — in that case we log replay.init now and
   *  defer replay.start until a later frame carries a real timestamp. */
  private loggedReplayInit = false;
  private loggedReplayStart = false;
  private loggedReplayEnd = false;
  /** Per-result terminal-line dedup. Populated by id, not original_request_id,
   *  so a row can move pending→running→ok and we still log a single done line. */
  private loggedResultIds = new Set<number>();
  /** Per-result start-line dedup. Fires the first time we see a result row
   *  with a started_at timestamp, regardless of which channel delivered it
   *  (SSE result frame, SSE replay-snapshot frame, polling snapshot, or
   *  initial fetch on deep-link). Without this, fast sequential runs that
   *  produce no SSE step events would get zero lines per request. */
  private loggedStartIds = new Set<number>();

  /**
   * Manual-mode gate state. Populated when the backend emits an
   * `awaiting_confirmation` SSE frame; cleared once the operator
   * advances/skips OR a result for the gated request lands. Holding
   * the request_id locally lets Advance/Skip POST the same id back so
   * the backend can reject stale clicks (request 5 sent for a gate
   * already on request 6).
   */
  manualGate: { requestId: number; method: string; deadline: number } | null = null;
  manualGateRemainingSec: number | null = null;
  manualGateError: string | null = null;
  manualSkipReason = '';
  manualActing = false;
  manualTimedOut = false;
  private manualCountdownSub: Subscription | null = null;

  constructor(private tracer: TracerService) {}

  ngOnInit(): void {
    if (this.initialReplayId !== null && this.initialReplayId !== undefined) {
      // Deep-link into a past run: skip the plan step and render the
      // snapshot directly. We still fetch the plan in the background so
      // the result rows can match against entry metadata (method,
      // provider, gap, …) — without it, selectedEntries() would be
      // empty and the result list would be blank.
      this.openExistingReplay(this.initialReplayId);
    } else {
      this.fetchPlan();
    }
  }

  /**
   * Hydrate panel state from an existing replay_id without going
   * through the plan step. Loads both the plan (for entry metadata)
   * and the replay snapshot (for status + result rows), then jumps
   * straight to the appropriate step (running for in-flight runs,
   * result for terminal ones — and resubscribes to SSE in the running
   * case so the user sees updates land).
   */
  private openExistingReplay(replayId: number): void {
    this.tracer.getReplay(this.sessionValue, replayId).subscribe({
      next: snap => {
        this.replay = snap.replay;
        this.resultsByRequestId = {};
        for (const r of snap.replay.results || []) {
          this.resultsByRequestId[r.original_request_id] = r;
        }
        this.resetLogState();
        // Pull the plan so we have method/provider/gap metadata to
        // render alongside the results. Mark every result-bearing
        // entry as "selected" so selectedEntries() includes them.
        this.tracer.getReplayPlan(this.sessionValue).subscribe({
          next: plan => {
            this.entries = plan.entries || [];
            this.defaultDateShiftDays = plan.default_date_shift_days;
            this.defaultRequestTimeoutMs = plan.default_request_timeout_ms;
            this.selected = {};
            for (const e of this.entries) {
              this.selected[e.id] = !!this.resultsByRequestId[e.id];
            }
            // Sync displayed pacing/shift parameters with the run.
            this.dateShiftDays = snap.replay.date_shift_days;
            this.pacingMode = snap.replay.pacing_mode;
            this.pacingFFwdMs = snap.replay.pacing_ffwd_ms;
            this.requestTimeoutSeconds = Math.max(
              1,
              Math.round(snap.replay.request_timeout_ms / 1000)
            );
            this.continueOnError = snap.replay.continue_on_error;
            // Rebuild the log AFTER the plan lands — we need
            // entries[] to look up method/provider context for each
            // terminal result line.
            this.rebuildLogFromSnapshot(snap.replay);
          },
          error: () => {
            /* plan fetch is best-effort; leave entries empty */
            this.rebuildLogFromSnapshot(snap.replay);
          },
        });
        if (this.isTerminal(snap.replay.status)) {
          this.step = 'result';
        } else {
          this.step = 'running';
          this.subscribeToProgress(replayId);
        }
      },
      error: err => {
        this.planError =
          err?.status === 403
            ? 'Repeat Session is admin-only.'
            : err?.error?.error || 'Could not load replay.';
      },
    });
  }

  ngOnDestroy(): void {
    this.cleanupStreams();
  }

  // -- Plan step -------------------------------------------------------

  fetchPlan(): void {
    if (!this.sessionValue) return;
    this.planLoading = true;
    this.planError = null;
    this.tracer.getReplayPlan(this.sessionValue).subscribe({
      next: res => {
        this.planLoading = false;
        this.entries = res.entries || [];
        this.defaultDateShiftDays = res.default_date_shift_days;
        this.defaultRequestTimeoutMs = res.default_request_timeout_ms;
        this.dateShiftDays = res.default_date_shift_days;
        this.requestTimeoutSeconds = Math.max(1, Math.round(res.default_request_timeout_ms / 1000));
        // Default selection: every replayable, non-destructive row.
        this.selected = {};
        for (const e of this.entries) {
          this.selected[e.id] = e.replayable && !e.destructive;
        }
      },
      error: err => {
        this.planLoading = false;
        this.planError =
          err?.status === 403
            ? 'Repeat Session is admin-only.'
            : err?.error?.error || 'Could not load replay plan.';
      },
    });
  }

  selectShoppingOnly(): void {
    for (const e of this.entries) {
      this.selected[e.id] = e.replayable && !e.destructive;
    }
  }

  selectAllReplayable(): void {
    for (const e of this.entries) {
      this.selected[e.id] = e.replayable;
    }
  }

  clearSelection(): void {
    for (const e of this.entries) {
      this.selected[e.id] = false;
    }
  }

  get selectedCount(): number {
    return Object.values(this.selected).filter(Boolean).length;
  }

  /** Number of rows the backend marks replayable (have raw_requests + body). */
  get replayableCount(): number {
    return this.entries.filter(e => e.replayable).length;
  }

  /**
   * True when the session has at least one request but none of them are
   * replayable — drives the empty-state banner so the user understands
   * the helper buttons + Run replay are inert because nothing can fire,
   * not because of a bug.
   */
  get nothingReplayable(): boolean {
    return this.entries.length > 0 && this.replayableCount === 0;
  }

  /** "Repeat session completely" — preselect everything replayable and start. */
  repeatCompletely(): void {
    this.selectAllReplayable();
    this.startReplay();
  }

  /**
   * Reset post-run state so the panel returns to a clean Plan step
   * ready for a fresh attempt. Called by the "Run again" button on
   * the result step. The operator's selection AND parameters are
   * intentionally preserved — re-running the same plan with one
   * tweaked parameter is the common iterate-on-failure case.
   *
   * What gets cleared:
   *   - replay (so the header pill no longer shows the previous
   *     run's terminal status)
   *   - resultsByRequestId / liveStepByRequestId (so per-row pills
   *     and substep text reset to defaults)
   *   - replayLog + the dedup sets / one-shots (so the log starts
   *     empty and the next markResult(RUNNING) re-emits the
   *     req.start line)
   *   - manualGate state (in case the previous run was manual)
   *   - any in-flight SSE / polling subscription
   *
   * What stays:
   *   - entries (the plan)
   *   - selected (which rows to replay)
   *   - dateShiftDays / pacingMode / pacingFFwdMs /
   *     requestTimeoutSeconds / continueOnError / verbosity
   *   - paramsExpanded (so the operator doesn't have to re-expand
   *     the parameters card on every iteration)
   *
   * Same intent as opening the dialog fresh, but without losing the
   * configuration the operator just tuned.
   */
  runAgain(): void {
    this.cleanupStreams();
    this.replay = null;
    this.resultsByRequestId = {};
    this.liveStepByRequestId = {};
    this.expandedResultIds = {};
    this.ioByResultId = {};
    this.startError = null;
    this.resetLogState();
    this.step = 'plan';
  }

  startReplay(): void {
    const requestIds = this.entries.filter(e => this.selected[e.id] && e.replayable).map(e => e.id);
    if (requestIds.length === 0) {
      this.startError = 'Select at least one replayable request.';
      return;
    }
    this.startError = null;
    const body: StartReplayBody = {
      request_ids: requestIds,
      date_shift_days: this.dateShiftDays,
      pacing_mode: this.pacingMode,
      pacing_ffwd_ms: this.pacingMode === 'ffwd' ? this.pacingFFwdMs : undefined,
      request_timeout_ms: this.requestTimeoutSeconds * 1000,
      continue_on_error: this.continueOnError,
      verbosity: this.verbosity,
    };
    this.tracer.startReplay(this.sessionValue, body).subscribe({
      next: res => {
        this.replay = res.replay;
        this.resultsByRequestId = {};
        this.liveStepByRequestId = {};
        this.resetLogState();
        this.logReplayInit(res.replay);
        this.step = 'running';
        this.subscribeToProgress(res.replay.id);
      },
      error: err => {
        if (err?.status === 409) {
          this.startError = 'A replay is already running for this session — wait for it to finish.';
        } else if (err?.status === 403) {
          this.startError = 'Not authorised: Repeat Session is admin-only.';
        } else {
          this.startError = err?.error?.error || 'Could not start replay.';
        }
      },
    });
  }

  // -- Live progress (SSE with polling fallback) -----------------------

  private subscribeToProgress(replayId: number): void {
    this.cleanupStreams();
    this.streamSub = this.tracer.streamReplay(this.sessionValue, replayId).subscribe({
      next: ev => this.applyEvent(ev),
      error: () => {
        // SSE failed (or finished) — resync with a single snapshot
        // read, and if the run is still active, fall back to polling.
        this.streamSub = null;
        this.refreshSnapshot(replayId, /*startPolling*/ true);
      },
      complete: () => {
        // Stream closed cleanly — resync once to make sure we have
        // the terminal state.
        this.streamSub = null;
        this.refreshSnapshot(replayId, /*startPolling*/ false);
      },
    });
  }

  private refreshSnapshot(replayId: number, startPolling: boolean): void {
    this.tracer.getReplay(this.sessionValue, replayId).subscribe({
      next: res => {
        this.applySnapshot(res.replay);
        if (this.isTerminal(res.replay.status)) {
          this.step = 'result';
          this.cleanupStreams();
        } else if (startPolling && !this.pollSub) {
          this.pollSub = interval(2000)
            .pipe(switchMap(() => this.tracer.getReplay(this.sessionValue, replayId)))
            .subscribe({
              next: r => {
                this.applySnapshot(r.replay);
                if (this.isTerminal(r.replay.status)) {
                  this.step = 'result';
                  this.cleanupStreams();
                }
              },
            });
        }
      },
    });
  }

  /**
   * Apply a full replay snapshot — sets replay/results, harvests log
   * lines from any new results we hadn't seen, and emits init/end
   * markers if we've crossed those thresholds. Used by every non-SSE
   * channel (refreshSnapshot, polling, openExistingReplay) so the log
   * gets populated regardless of how the data arrives.
   *
   * Idempotent: dedup sets (loggedStartIds / loggedResultIds /
   * loggedReplayInit / loggedReplayEnd) absorb repeated calls without
   * producing duplicate lines.
   */
  private applySnapshot(replay: SessionReplay): void {
    this.replay = replay;
    this.resultsByRequestId = {};
    for (const r of replay.results || []) {
      this.resultsByRequestId[r.original_request_id] = r;
    }
    this.logReplayInit(replay);
    this.harvestResults(replay.results || []);
    this.logReplayEndIfTerminal(replay);
  }

  /**
   * Harvest log lines from a batch of result rows. Idempotent — both
   * the start and the done lines per result are deduped, so this is
   * safe to call on every snapshot/polling tick.
   */
  private harvestResults(results: readonly ReplayRequestResult[]): void {
    for (const r of results) {
      this.logResultStartIfNew(r);
      this.logResultIfTerminal(r);
    }
  }

  /**
   * Status-progression rank — pending < running < {ok, error,
   * skipped, skipped_by_user, cancelled}. Used by mergeResult to
   * reject a stale snapshot row that would downgrade a row we've
   * already seen progress on via a discrete `result` event.
   *
   * Equal rank still updates (newer fields like http_status,
   * duration, error string may have arrived alongside the same
   * status). Only a strictly-lower rank is rejected.
   */
  private statusRank(status: string | undefined): number {
    switch (status) {
      case 'pending':
        return 0;
      case 'running':
        return 1;
      case 'ok':
      case 'error':
      case 'skipped':
      case 'skipped_by_user':
      case 'cancelled':
        return 2;
      default:
        return 0;
    }
  }

  /**
   * Merge an incoming result row into resultsByRequestId. Never
   * downgrades: if the existing row has a higher status rank, the
   * incoming one is dropped silently.
   *
   * Why this isn't just `resultsByRequestId[id] = r`: snapshot frames
   * (from SSE `replay` events or polling-fallback refreshSnapshot) read
   * the DB at one instant and ship every row's then-current state.
   * Between the DB read and the SSE frame's arrival, a discrete
   * `result` event for the same row may already have landed at the FE
   * with a more-progressed status. Blind assignment would replace the
   * fresher row with the stale snapshot copy and the pill would
   * regress (the user-reported "pending while running" was the inverse
   * — snapshot HAD the running but never made it into the map — but
   * the merge guard protects both directions).
   */
  private mergeResult(incoming: ReplayRequestResult): void {
    const id = incoming.original_request_id;
    const existing = this.resultsByRequestId[id];
    if (!existing) {
      this.resultsByRequestId[id] = incoming;
      return;
    }
    if (this.statusRank(incoming.status) >= this.statusRank(existing.status)) {
      this.resultsByRequestId[id] = incoming;
    }
  }

  private applyEvent(ev: ReplayEvent): void {
    if (ev.replay) {
      this.replay = ev.replay;
      this.logReplayInit(ev.replay);
      // Snapshot frames carry result rows inline (tracer MR !406).
      // BOTH paths matter:
      //   - mergeResult: populates resultsByRequestId so the row's
      //     status pill renders. Without this, the BE sends a
      //     snapshot with seq=0 already running, but the FE never
      //     transitions the pill out of 'pending' because the map
      //     stays empty — even though the per-row step text
      //     ("Awaiting response…") updates correctly via the
      //     separate liveStepByRequestId / step-event path. That
      //     mismatched-state symptom was the user-reported bug.
      //   - harvestResults: produces req.start / req.done log lines
      //     for any new terminal rows (unchanged).
      for (const r of ev.replay.results || []) {
        this.mergeResult(r);
      }
      this.harvestResults(ev.replay.results || []);
      if (this.isTerminal(ev.replay.status)) {
        this.step = 'result';
        this.clearManualGate();
        this.logReplayEndIfTerminal(ev.replay);
      }
    }
    if (ev.result) {
      this.mergeResult(ev.result);
      this.logResultStartIfNew(ev.result);
      this.logResultIfTerminal(ev.result);
      // A result for the gated request means the executor moved past
      // the gate (advance fired the request, OR skip recorded the
      // skipped_by_user row). Clear the gate either way.
      if (this.manualGate && ev.result.original_request_id === this.manualGate.requestId) {
        this.clearManualGate();
      }
    }
    if (ev.type === 'awaiting_confirmation' && ev.request_id !== undefined) {
      const timeoutMs = (ev.timeout_seconds || 60) * 1000;
      this.manualGate = {
        requestId: ev.request_id,
        method: ev.method || 'request',
        deadline: Date.now() + timeoutMs,
      };
      this.manualGateError = null;
      this.manualSkipReason = '';
      this.startManualCountdown();
      this.appendLog(
        'info',
        `req.gate.wait   req_id=${ev.request_id}` +
          (ev.method ? ` method=${ev.method}` : '') +
          ` timeout=${ev.timeout_seconds || 60}s`
      );
    }
    if (ev.type === 'session_linked' && ev.linked_session_value && this.replay) {
      // Reflect the carry-forward link immediately so the user sees the
      // chip without waiting for the next replay-snapshot frame.
      this.replay = { ...this.replay, linked_session_value: ev.linked_session_value };
      this.appendLog('info', `replay.linked   linked_session=${ev.linked_session_value}`);
    }
    if (ev.type === 'replay_timeout') {
      this.manualTimedOut = true;
      this.clearManualGate();
      this.appendLog(
        'warn',
        `replay.timeout  ${ev.request_id !== undefined ? `req_id=${ev.request_id}` : ''}`.trim()
      );
    }
    if (ev.type === 'step' && ev.request_id !== undefined && ev.step) {
      // Mid-request activity update from the executor. We carry it on
      // a side map (not on the result row) because the row may not
      // exist yet at the very first phase, and because step is
      // transient — the eventual `result` frame supersedes it.
      this.liveStepByRequestId[ev.request_id] = ev.step;
      this.logStepIfNew(ev.request_id, ev.step);
    }
    if (ev.type === 'error' && ev.error) {
      this.appendLog('error', `stream.error    err="${this.escapeQuotes(ev.error)}"`);
    }
  }

  /**
   * Human-readable label for a step constant. Used by the running
   * step's italic gray status line under each row.
   */
  stepLabel(step: string | undefined, hostHint?: string): string {
    switch (step) {
      case 'loading_payload':
        return 'Recovering raw payload from CDN…';
      case 'shifting_dates':
        return 'Shifting travel dates…';
      case 'pacing_wait':
        return 'Pacing wait…';
      case 'awaiting_manual':
        return 'Awaiting confirmation…';
      case 'firing_http':
        return hostHint ? `Sending HTTP request to ${hostHint}…` : 'Sending HTTP request…';
      case 'awaiting_response':
        return 'Awaiting response…';
      case 'received_response':
        return 'Response received';
      case 'persisting':
        return 'Persisting result…';
      default:
        return '';
    }
  }

  /**
   * Step text for the row identified by entry.id. Prefers the live
   * SSE-provided value, falls back to the persisted current_step on
   * the row (matters for late SSE joiners + post-snapshot reads).
   */
  stepFor(entry: ReplayPlanEntry): string {
    const live = this.liveStepByRequestId[entry.id];
    if (live) return this.stepLabel(live, entry.provider || undefined);
    const result = this.resultsByRequestId[entry.id];
    return this.stepLabel(result?.current_step, entry.provider || undefined);
  }

  // -- Manual-mode controls -------------------------------------------

  advanceCurrentManualStep(): void {
    if (!this.manualGate || !this.replay || this.manualActing) return;
    const requestId = this.manualGate.requestId;
    this.manualActing = true;
    this.manualGateError = null;
    this.tracer
      .advanceManualReplay(this.sessionValue, this.replay.id, { request_id: requestId })
      .subscribe({
        next: () => {
          this.manualActing = false;
          // Don't clear manualGate here — wait for the SSE result
          // frame to land so the gate transitions atomically with
          // the result row.
        },
        error: err => {
          this.manualActing = false;
          this.manualGateError = this.manualGateErrorMessage(err);
        },
      });
  }

  skipCurrentManualStep(): void {
    if (!this.manualGate || !this.replay || this.manualActing) return;
    const requestId = this.manualGate.requestId;
    this.manualActing = true;
    this.manualGateError = null;
    this.tracer
      .skipManualReplay(this.sessionValue, this.replay.id, {
        request_id: requestId,
        reason: this.manualSkipReason || undefined,
      })
      .subscribe({
        next: () => {
          this.manualActing = false;
        },
        error: err => {
          this.manualActing = false;
          this.manualGateError = this.manualGateErrorMessage(err);
        },
      });
  }

  private manualGateErrorMessage(err: unknown): string {
    const e = err as { status?: number; error?: { error?: string } };
    if (e?.status === 409) return 'No request is awaiting confirmation right now.';
    if (e?.status === 410) return 'Replay has already finished.';
    if (e?.status === 404) return 'Replay not found.';
    if (e?.status === 400) return 'This replay is not running in manual mode.';
    return e?.error?.error || 'Could not act on the gate.';
  }

  private startManualCountdown(): void {
    this.manualCountdownSub?.unsubscribe();
    const tick = () => {
      if (!this.manualGate) {
        this.manualGateRemainingSec = null;
        return;
      }
      const remaining = Math.max(0, Math.ceil((this.manualGate.deadline - Date.now()) / 1000));
      this.manualGateRemainingSec = remaining;
    };
    tick();
    this.manualCountdownSub = interval(1000).subscribe(tick);
  }

  private clearManualGate(): void {
    this.manualGate = null;
    this.manualGateRemainingSec = null;
    this.manualGateError = null;
    this.manualCountdownSub?.unsubscribe();
    this.manualCountdownSub = null;
  }

  private isTerminal(status: string): boolean {
    return status === 'succeeded' || status === 'partial' || status === 'failed';
  }

  private cleanupStreams(): void {
    this.streamSub?.unsubscribe();
    this.streamSub = null;
    this.pollSub?.unsubscribe();
    this.pollSub = null;
    this.clearManualGate();
  }

  // -- Result row interactions ----------------------------------------

  toggleExpand(result: ReplayRequestResult): void {
    const id = result.id;
    this.expandedResultIds[id] = !this.expandedResultIds[id];
    if (this.expandedResultIds[id] && this.verbosity === 'verbose' && !this.ioByResultId[id]) {
      this.fetchIO(result);
    }
  }

  setVerbosity(level: ReplayVerbosity): void {
    this.verbosity = level;
    // Lazy-load IO for already-expanded rows when bumping to verbose.
    if (level === 'verbose') {
      for (const idStr of Object.keys(this.expandedResultIds)) {
        const id = Number(idStr);
        if (this.expandedResultIds[id] && !this.ioByResultId[id]) {
          const r = this.findResultById(id);
          if (r) this.fetchIO(r);
        }
      }
    }
  }

  private findResultById(id: number): ReplayRequestResult | undefined {
    for (const r of Object.values(this.resultsByRequestId)) {
      if (r.id === id) return r;
    }
    return undefined;
  }

  /**
   * Cap retries on 202-capture-pending. Sidecar capture is normally
   * sub-second, but on a heavily-loaded staging the gap can stretch
   * to ~5s. Cap saves a runaway poll if the sidecar genuinely never
   * lands the row (route misconfig, sidecar down, etc.).
   */
  private static readonly IO_MAX_RETRIES = 6;
  private static readonly IO_RETRY_DELAY_MS = 1500;

  private fetchIO(result: ReplayRequestResult, attempt = 0): void {
    if (!this.replay) return;
    if (attempt === 0) {
      this.ioByResultId[result.id] = { loading: true };
    }
    this.tracer.getReplayResultIO(this.sessionValue, this.replay.id, result.id).subscribe({
      next: io => {
        // The backend signals "capture pending" with status 202 +
        // {error, replay_request_id} (no request/response). Angular's
        // HttpClient delivers 202 to `next`, so we discriminate on the
        // body shape: if neither side of the I/O is present and we
        // have an error string, retry up to IO_MAX_RETRIES.
        const stillCapturing = !io.request && !io.response && !!io.error;
        if (stillCapturing && attempt < ReplayPanel.IO_MAX_RETRIES) {
          setTimeout(() => this.fetchIO(result, attempt + 1), ReplayPanel.IO_RETRY_DELAY_MS);
          return;
        }
        this.ioByResultId[result.id] = io;
      },
      error: err => {
        this.ioByResultId[result.id] = {
          error: err?.error?.error || 'Could not load full I/O.',
        };
      },
    });
  }

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text).catch(() => {});
  }

  // -- View helpers ---------------------------------------------------

  /**
   * Tailwind classes for the status pill — mirrors the GetStatusPipe
   * palette so this dialog feels consistent with the request list.
   */
  statusClasses(status: string | undefined): string {
    switch (status) {
      case 'ok':
        return 'bg-green-100 text-green-800';
      case 'error':
        return 'bg-red-100 text-red-800';
      case 'running':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-gray-100 text-gray-800';
      case 'skipped':
      case 'skipped_by_user':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        // Distinct slate tone — terminal but neither success, error,
        // nor a per-row "skipped" decision. Reading: "the run stopped
        // before the executor got here." Slate visually separates
        // these from the yellow `skipped` family so operators can
        // tell at a glance which rows were a deliberate skip vs
        // collateral cancellation from an upstream failure.
        return 'bg-slate-200 text-slate-700';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  replayStatusClasses(status: string | undefined): string {
    switch (status) {
      case 'succeeded':
        return 'bg-green-100 text-green-800';
      case 'partial':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'running':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  /** "5 / 7 done · 1 error" summary for the running/result steps. */
  get summaryLine(): string {
    const total = this.entries.filter(e => this.selected[e.id]).length;
    const done = Object.values(this.resultsByRequestId).filter(r =>
      ['ok', 'error', 'skipped', 'skipped_by_user', 'cancelled'].includes(r.status)
    ).length;
    const errors = Object.values(this.resultsByRequestId).filter(r => r.status === 'error').length;
    const pacingLabel = this.pacingLabel(this.replay?.pacing_mode || this.pacingMode);
    return `${done} / ${total} done · ${errors} error${errors === 1 ? '' : 's'} · pacing: ${pacingLabel}`;
  }

  private pacingLabel(mode: PacingMode): string {
    switch (mode) {
      case 'realtime':
        return 'real-time';
      case 'ffwd':
        return `ffwd ${this.pacingFFwdMs}ms`;
      case 'sequential':
      case 'instant':
        return 'sequential';
      case 'manual':
        return 'manual';
    }
  }

  resultFor(entry: ReplayPlanEntry): ReplayRequestResult | undefined {
    return this.resultsByRequestId[entry.id];
  }

  /** Format a ms gap as "Xs" or "Xms". */
  formatGap(ms: number): string {
    if (!ms) return '—';
    if (ms < 1000) return `${ms}ms`;
    const s = ms / 1000;
    if (s < 60) return `${s.toFixed(1)}s`;
    const m = s / 60;
    return `${m.toFixed(1)}m`;
  }

  // entries iteration in template (avoids strict object-key typing churn)
  selectedEntries(): ReplayPlanEntry[] {
    return this.entries.filter(e => this.selected[e.id]);
  }

  // Template type narrowing for the verbose IO union. Returning the
  // value (or null) from each helper lets the template's @let / @if
  // bind cleanly.
  asLoading(io: ResultIO | { loading: true } | { error: string }): boolean {
    return (io as { loading?: boolean }).loading === true;
  }

  asError(io: ResultIO | { loading: true } | { error: string }): string | null {
    return (io as { error?: string }).error || null;
  }

  asIO(io: ResultIO | { loading: true } | { error: string }): ResultIO | null {
    if ((io as { loading?: boolean }).loading || (io as { error?: string }).error) {
      return null;
    }
    return io as ResultIO;
  }

  // -- Replay log -----------------------------------------------------

  /**
   * Reset all log-related state. Called when starting a fresh run or
   * when hydrating an existing replay so the panel never shows stale
   * lines from a previous open.
   */
  private resetLogState(): void {
    this.replayLog = [];
    this.lastLoggedStep = {};
    this.loggedReplayInit = false;
    this.loggedReplayStart = false;
    this.loggedReplayEnd = false;
    this.loggedResultIds = new Set();
    this.loggedStartIds = new Set();
    this.copiedLog = false;
  }

  /**
   * Go's encoding/json serialises a zero `time.Time{}` as the literal
   * "0001-01-01T00:00:00Z" string when the field doesn't carry
   * `omitempty`. We see this on result rows that exist as pending
   * placeholders (no started_at yet) and on replay snapshots before
   * the executor crosses the start boundary. Treat that string as
   * "not set" everywhere so we don't (a) stamp log lines with a
   * useless 0001 timestamp or (b) prematurely add the row to the
   * dedup sets and lock out the eventual real timestamp.
   */
  private static readonly GO_ZERO_TIME = '0001-01-01T00:00:00Z';
  private isZeroTime(ts: string | undefined | null): boolean {
    if (!ts) return true;
    return ts === ReplayPanel.GO_ZERO_TIME || ts.startsWith('0001-01-01T00:00:00');
  }

  private appendLog(level: LogLevel, msg: string, ts?: string): void {
    // Defensive: if a caller slips in a zero/empty timestamp we
    // substitute the wall-clock time before storing. Better an
    // approximate "logged at" than a 0001-01-01 footgun.
    const raw = !this.isZeroTime(ts) ? (ts as string) : new Date().toISOString();
    this.replayLog = [...this.replayLog, { ts: this.normaliseTs(raw), level, msg }];
  }

  /**
   * Normalise an ISO-8601 timestamp to fixed-width millisecond
   * precision: "YYYY-MM-DDTHH:mm:ss.sssZ" (24 chars exactly).
   *
   * Why this matters: timestamps reach the log from three sources
   * with three different precisions, which made columns drift in the
   * copyable log output:
   *
   *   - Go time.Now() formatted with nanoseconds  → 30 chars
   *     (e.g. "2026-05-10T22:17:36.764403656Z")
   *   - Postgres TIMESTAMPTZ with microseconds    → 27 chars
   *     (e.g. "2026-05-10T22:17:36.773036Z")
   *   - JS new Date().toISOString()               → 24 chars
   *     (e.g. "2026-05-10T22:17:36.747Z")
   *
   * Coercing every line through Date.toISOString() collapses all
   * three to the 24-char form. Operators get a fixed left margin so
   * the LEVEL / tag / key=value columns line up regardless of which
   * source the timestamp came from.
   *
   * Sub-millisecond precision is dropped (no operational signal at
   * that resolution given Postgres TIMESTAMPTZ already rounds to the
   * microsecond on insert; Go's nanoseconds are CPU-clock artefacts
   * that don't survive the round-trip anyway).
   */
  private normaliseTs(ts: string): string {
    const parsed = new Date(ts);
    if (isNaN(parsed.getTime())) {
      // Unparseable timestamp — fall back to "now" rather than
      // leaving a wonky raw string in the log.
      return new Date().toISOString();
    }
    return parsed.toISOString();
  }

  private logReplayInit(replay: SessionReplay): void {
    // The init line fires once per replay on the first frame we see.
    // The start line is gated separately because the BE may emit the
    // first replay snapshot with started_at still empty (executor
    // hasn't crossed the start boundary). On that frame we log init
    // immediately and defer start to a later frame that carries a
    // real timestamp — without that gate we used to stamp the start
    // line with "0001-01-01T00:00:00Z" once, then never re-emit it.
    if (!this.loggedReplayInit) {
      this.loggedReplayInit = true;
      const timeoutSec = Math.max(1, Math.round(replay.request_timeout_ms / 1000));
      const fields = [
        `id=${replay.id}`,
        `pacing=${replay.pacing_mode}`,
        replay.pacing_mode === 'ffwd' ? `ffwd_ms=${replay.pacing_ffwd_ms}` : null,
        `shift=${replay.date_shift_days}d`,
        `timeout=${timeoutSec}s`,
        `continue_on_error=${replay.continue_on_error}`,
        `verbosity=${replay.verbosity || 'detailed'}`,
        `target_env=${replay.target_env || '—'}`,
        `triggered_by=${replay.triggered_by || '—'}`,
      ]
        .filter(Boolean)
        .join(' ');
      this.appendLog('info', `replay.init     ${fields}`, replay.created_at);
    }
    if (!this.loggedReplayStart && !this.isZeroTime(replay.started_at)) {
      this.loggedReplayStart = true;
      this.appendLog('info', `replay.start    id=${replay.id}`, replay.started_at);
    }
  }

  private logReplayEndIfTerminal(replay: SessionReplay): void {
    if (this.loggedReplayEnd) return;
    if (!this.isTerminal(replay.status)) return;
    this.loggedReplayEnd = true;
    const total = (replay.results || []).length;
    const errors = (replay.results || []).filter(r => r.status === 'error').length;
    const fields = [
      `id=${replay.id}`,
      `status=${replay.status}`,
      `done=${total}`,
      `errors=${errors}`,
      replay.linked_session_value ? `linked_session=${replay.linked_session_value}` : null,
      replay.error ? `err="${this.escapeQuotes(replay.error)}"` : null,
    ]
      .filter(Boolean)
      .join(' ');
    this.appendLog(
      replay.status === 'failed' ? 'error' : 'info',
      `replay.end      ${fields}`,
      replay.completed_at
    );
  }

  /**
   * "req.start" line — fires the first time we see a result row with a
   * started_at timestamp. Sourced from any channel: discrete SSE
   * `result` event, snapshot frame's inline `replay.results[]`, polling
   * tick, or the initial deep-link fetch. Without this, runs that
   * produce no SSE step events (fast sequential pacing, polling-only
   * fallback) had no per-request lines until the terminal state
   * appeared, leaving long stretches of dead-air log.
   */
  private logResultStartIfNew(r: ReplayRequestResult): void {
    // Gate dedup on a NON-ZERO timestamp. Pending result rows surface
    // with started_at = "0001-01-01T00:00:00Z" (Go's serialised zero
    // time, the BE inserts placeholders before the executor reaches
    // them). Previously we treated any non-empty string as "set",
    // logged the line with the zero stamp, and locked the row out of
    // ever re-logging when the real timestamp landed on a later
    // snapshot. Now we wait for a real timestamp before logging AND
    // before adding to loggedStartIds.
    if (this.isZeroTime(r.started_at)) return;
    if (this.loggedStartIds.has(r.id)) return;
    this.loggedStartIds.add(r.id);
    const entry = this.entries.find(e => e.id === r.original_request_id);
    const fields = [
      `seq=${r.sequence}`,
      `req_id=${r.original_request_id}`,
      entry?.method ? `method=${entry.method}` : null,
      entry?.provider ? `provider=${entry.provider}` : null,
    ]
      .filter(Boolean)
      .join(' ');
    this.appendLog('info', `req.start       ${fields}`, r.started_at);
  }

  private logResultIfTerminal(r: ReplayRequestResult): void {
    const terminal = ['ok', 'error', 'skipped', 'skipped_by_user', 'cancelled'].includes(r.status);
    if (!terminal) return;
    if (this.loggedResultIds.has(r.id)) return;
    this.loggedResultIds.add(r.id);
    const entry = this.entries.find(e => e.id === r.original_request_id);
    const fields = [
      `seq=${r.sequence}`,
      `req_id=${r.original_request_id}`,
      entry?.method ? `method=${entry.method}` : null,
      entry?.provider ? `provider=${entry.provider}` : null,
      `status=${r.status}`,
      r.http_status !== undefined ? `http=${r.http_status}` : null,
      r.duration_ms !== undefined ? `duration=${r.duration_ms}ms` : null,
      r.replay_request_id ? `x_request_id=${r.replay_request_id}` : null,
      r.error ? `err="${this.escapeQuotes(r.error)}"` : null,
    ]
      .filter(Boolean)
      .join(' ');
    // Log level mapping:
    //   error                                    → 'error'
    //   skipped / skipped_by_user / cancelled    → 'warn'
    //   ok                                       → 'info'
    let level: LogLevel = 'info';
    if (r.status === 'error') {
      level = 'error';
    } else if (r.status.startsWith('skipped') || r.status === 'cancelled') {
      level = 'warn';
    }
    this.appendLog(level, `req.done        ${fields}`, r.completed_at);
  }

  private logStepIfNew(requestId: number, step: string): void {
    if (this.lastLoggedStep[requestId] === step) return;
    this.lastLoggedStep[requestId] = step;
    const entry = this.entries.find(e => e.id === requestId);
    const fields = [
      `req_id=${requestId}`,
      entry?.method ? `method=${entry.method}` : null,
      entry?.provider ? `provider=${entry.provider}` : null,
      `step=${step}`,
    ]
      .filter(Boolean)
      .join(' ');
    this.appendLog('info', `req.step        ${fields}`);
  }

  /**
   * Rebuild a coarse log from a finished snapshot — terminal events
   * only, no step history (we don't have it for past runs). Used by
   * the deep-link path so opening a finished replay produces the same
   * paste-able trace operators see during a live run.
   */
  private rebuildLogFromSnapshot(replay: SessionReplay): void {
    this.logReplayInit(replay);
    const ordered = [...(replay.results || [])].sort((a, b) => a.sequence - b.sequence);
    for (const r of ordered) {
      this.logResultStartIfNew(r);
      this.logResultIfTerminal(r);
    }
    if (
      replay.linked_session_value &&
      !this.replayLog.some(l => l.msg.startsWith('replay.linked'))
    ) {
      this.appendLog('info', `replay.linked   linked_session=${replay.linked_session_value}`);
    }
    this.logReplayEndIfTerminal(replay);
  }

  private escapeQuotes(s: string): string {
    return s.replace(/"/g, "'");
  }

  /** "[ts] LEVEL  message" — single string suitable for clipboard. */
  get replayLogText(): string {
    return this.replayLog.map(l => `[${l.ts}] ${l.level.padEnd(5, ' ')} ${l.msg}`).join('\n');
  }

  copyLog(): void {
    if (!this.replayLog.length) return;
    navigator.clipboard
      .writeText(this.replayLogText)
      .then(() => {
        this.copiedLog = true;
        setTimeout(() => (this.copiedLog = false), 1500);
      })
      .catch(() => {
        /* clipboard denied — nothing graceful to do here */
      });
  }

  logLineClasses(level: LogLevel): string {
    switch (level) {
      case 'error':
        return 'text-red-700';
      case 'warn':
        return 'text-amber-700';
      case 'info':
      default:
        return 'text-gray-700';
    }
  }
}
